import { useState } from "react";
import { Wrench, CheckCircle2, Circle, AlertTriangle, ListChecks } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { CamberCasterGuide } from "../components/CamberCasterGuide";

const TOOLS = [
  "Digital angle gauge / inclinometer",
  "2× turn plates (or greased steel plates)",
  "String line + jack stands (string box)",
  "Tape measure (steel)",
  "24 mm & 21 mm wrenches / sockets",
  "Torque wrench (up to 150 lb-ft)",
  "Breaker bar + penetrating oil",
];

const STEPS = [
  { title: "Prep & ride height", caution: "Full fuel tank, level floor.",
    body: "Park on a flat, level surface. Fill the tank (or add compensating load). Jounce the front and rear bumpers 3× each so the suspension settles. Verify Z/D ride height before touching anything — correct ride height first with the torsion bar keys if the nose sags." },
  { title: "Set up measurement", caution: null,
    body: "Place turn plates under the front wheels and smooth plates under the rear. Set up your string box so both strings run parallel to the vehicle centerline, equidistant from the hubs front and rear." },
  { title: "Read the rear first", caution: "Rear is a solid axle — not adjustable.",
    body: "On a 4-wheel alignment always reference the rear axle first. Measure rear camber and total toe. If rear readings are out of spec, inspect the trailing arms and axle for damage — there is no adjustment." },
  { title: "Measure front caster & camber", caution: "Compensate for frame angle.",
    body: "Put a digital gauge on a flat frame section to find the frame angle, then measure caster relative to it. Measure camber on each front wheel with the gauge against the rotor/hub face." },
  { title: "Adjust front caster/camber", caution: "Cam bolt nut: 140 lb-ft.", torque: "140 lb-ft",
    body: "Loosen the upper control arm eccentric cam bolts and rotate the cams. Front cam = caster split, rear cam = camber (they interact, so iterate). Hold the bolt head steady and torque the nut to 190 N·m (140 lb-ft)." },
  { title: "Set front toe", caution: "Tie rod jam nut: 50 lb-ft.", torque: "50 lb-ft",
    body: "With the steering wheel centered and locked, loosen the tie rod jam nuts and rotate the inner tie rod sleeves. Adjust both sides equally to keep the wheel straight. Target +0.10° total toe, then torque jam nuts to 68 N·m (50 lb-ft)." },
  { title: "Verify & road test", caution: null,
    body: "Re-jounce, recheck all angles, confirm cross-camber and cross-caster are within 0.50°. Road test on a straight, flat road — the steering wheel should sit level with no pull. Re-check toe if you touched caster." },
];

const TROUBLESHOOT = [
  { q: "Steering wheel off-center (car tracks straight)", a: "Center the wheel and adjust both front tie rods by equal and opposite amounts — lengthen one side, shorten the other — until the wheel is level without changing total toe." },
  { q: "Vehicle pulls to one side", a: "Usually a caster or camber split. Increase caster on the side it pulls toward, or reduce camber difference. Confirm equal tire pressures and rule out a dragging brake first." },
  { q: "Inner/outer tire edge wearing fast", a: "Excess camber (edge wear) or incorrect toe (feathering). Re-measure camber and set toe to +0.10°. On a lifted H2, check for maxed-out cam adjustment — you may need adjustable arms." },
  { q: "Darting / wandering at highway speed", a: "Too little positive caster or worn steering components. Verify caster is 2.5–3.5° and inspect the idler/pitman arms, tie rod ends and ball joints for play." },
];

export default function Guide() {
  const [done, setDone] = useState({});
  const completed = Object.values(done).filter(Boolean).length;
  const pct = Math.round((completed / STEPS.length) * 100);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
          <Wrench size={14} /> Garage Procedure
        </div>
        <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">DIY Alignment Guide</h1>
        <p className="text-slate-400 text-sm mt-2">String-line alignment procedure for your selected vehicle. Check off each step as you go.</p>
      </div>

      {/* progress */}
      <div className="rounded-md bg-surface border border-steel p-5 mb-6 animate-rise">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-xs text-slate-400 tracking-widest uppercase flex items-center gap-2"><ListChecks size={15} className="text-amber" /> Progress</span>
          <span className="font-mono text-sm text-amber tabular-nums">{completed}/{STEPS.length} · {pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-ink overflow-hidden">
          <div className="h-full bg-amber transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* alignment basics illustration */}
      <CamberCasterGuide />

      {/* tools */}
      <section className="rounded-md bg-surface border border-steel p-5 sm:p-6 mb-6 animate-rise">
        <h2 className="font-heading font-bold uppercase tracking-widest text-lg mb-4">Tools Needed</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TOOLS.map((t) => (
            <div key={t} className="flex items-center gap-2 font-mono text-xs text-slate-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber shrink-0" /> {t}
            </div>
          ))}
        </div>
      </section>

      {/* steps */}
      <div className="space-y-3 mb-8">
        {STEPS.map((step, i) => {
          const isDone = !!done[i];
          return (
            <div key={i} data-testid={`step-${i}`}
              className={`rounded-md border p-5 transition-colors animate-rise ${isDone ? "bg-pass/5 border-pass/40" : "bg-surface border-steel"}`}>
              <div className="flex items-start gap-4">
                <button data-testid={`step-toggle-${i}`} onClick={() => setDone((p) => ({ ...p, [i]: !p[i] }))}
                  className="shrink-0 mt-0.5 transition-transform hover:scale-110">
                  {isDone ? <CheckCircle2 size={24} className="text-pass" /> : <Circle size={24} className="text-slate-600" />}
                </button>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-amber text-xs">STEP {i + 1}</span>
                    {step.torque && (
                      <span className="font-mono text-[10px] text-sky bg-sky/10 px-2 py-0.5 rounded-sm tracking-widest">TORQUE {step.torque}</span>
                    )}
                  </div>
                  <h3 className={`font-heading font-semibold text-lg mt-1 ${isDone ? "line-through text-slate-500" : "text-white"}`}>{step.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{step.body}</p>
                  {step.caution && (
                    <div className="flex items-center gap-2 mt-3 font-mono text-[11px] text-amber">
                      <AlertTriangle size={13} /> {step.caution}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* troubleshooting */}
      <section className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
        <h2 className="font-heading font-bold uppercase tracking-widest text-lg mb-4">Troubleshooting</h2>
        <Accordion type="single" collapsible className="w-full">
          {TROUBLESHOOT.map((t, i) => (
            <AccordionItem key={i} value={`t-${i}`} data-testid={`troubleshoot-${i}`} className="border-steel">
              <AccordionTrigger className="font-mono text-sm text-slate-200 hover:text-amber hover:no-underline text-left">
                {t.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-400 leading-relaxed">{t.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
